import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { Note } from "../../../components/primitives/data-display/Note";
import { Statistic } from "../../../components/primitives/data-display/Statistic";
import { ConfirmDialog } from "../../../components/primitives/feedback/ConfirmDialog";
import { gaps } from "../../../components/primitives/spacing";
import { Panel } from "../../../components/primitives/surfaces/Panel";
import { CORPUS_SCAN_HEADINGS } from "./corpus_scan_headings";
import { CorpusScanPanel } from "./CorpusScanPanel";
import { describeMissingSectionFilesScan } from "./missing_section_files_scan_views";
import type { UseMissingSectionFilesScanResult } from "./useMissingSectionFilesScan";

interface MissingSectionFilesScanProps {
  scan: UseMissingSectionFilesScanResult;
}

/** The scan for the documents that lost a section file in Storage, with the button that fetches
 *  their sections again. */
export function MissingSectionFilesScan({ scan }: MissingSectionFilesScanProps) {
  const { result, scan: run, launch, dialogProps } = scan;
  const view = result ? describeMissingSectionFilesScan(result) : null;

  return (
    <>
      <CorpusScanPanel
        {...CORPUS_SCAN_HEADINGS.missing_section_files}
        runLabel="Scan storage"
        onRun={() => void run.handle()}
        running={run.loading}
        error={run.error}
        results={
          view ? (
            <Panel>
              <Stack gap={gaps.base}>
                <Stack direction="row" gap={4} flexWrap="wrap">
                  <Statistic label="checked" mono>
                    {view.checked}
                  </Statistic>
                  <Statistic label="missing the judge file" mono>
                    {view.missingJudge}
                  </Statistic>
                  <Statistic label="missing the excluded file" mono>
                    {view.missingExcluded}
                  </Statistic>
                  <Statistic label="missing either" mono>
                    {view.missingAny}
                  </Statistic>
                </Stack>
                {view.launchLabel ? (
                  <Button variant="outlined" onClick={launch} sx={{ alignSelf: "flex-start" }}>
                    {view.launchLabel}
                  </Button>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Every checked document has both section files.
                  </Typography>
                )}
                <Note>
                  The claim search judges a document from its judge file, and the document view also
                  reads the excluded one. Refreshing fetches the document again, rewrites both files
                  and embeds it again.
                </Note>
              </Stack>
            </Panel>
          ) : null
        }
      />
      <ConfirmDialog {...dialogProps} />
    </>
  );
}
